import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { style } from 'typestyle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import assert from 'assert';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import Switch from 'react-switch';
import { MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import LockOutlineIcon from 'mdi-react/LockOutlineIcon';
import CalendarClockIcon from 'mdi-react/CalendarClockIcon';
import AccountGroupOutlineIcon from 'mdi-react/AccountGroupOutlineIcon';
import * as styles from '../offices.styles';
import * as formStyles from '../office-form.styles';
import OfficeFormType from '../enums/office-form-types';
import { useCompanies } from '../../companies/hooks/companies.hook';
import { useOfficeManagerOrAdminOffices } from '../hooks/offices.hook';
import type Office from '../models/office';
import { UpdateLocalStorageWithOffice, ApplyValueToOffice, NavigateToEdit, groupPlanPeriodDaysError } from '../office.component';
import { createOffice, updateOffice } from '../services/offices.service';
import OFFICE_INFORMATION_KEY from '../consts/consts';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import { Center } from '../../../core/components/center/center.component';
import notify from '../../../core/components/notify/notify.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import isThresholdPairValid from '../../../core/utils/threshold-utils';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import AppConstants from '../../../core/app.constants';
import TimezoneSelector from '../components/timezone-selector.component';
import { defaultBookingTimeZone } from '../models/office.constants';

interface Props {
  readonly isCreatingOffice: boolean;
  readonly officeId: string | undefined;
  setCanEditRoomCapacity: (canEdit: boolean) => void;
  readonly office: Office | undefined;
  readonly onSaved?: () => void;
  /** Seats across this office's rooms — turns the capacity percentage into real numbers. */
  readonly totalSeats?: number;
  /** Rendered under Save in the summary rail. The owner keeps the handler and the confirmation. */
  readonly deleteAction?: React.ReactNode;
}

const clampPercentage = (value: number): number => Math.min(100, Math.max(0, value));

const OfficeInfoGroup = ({
  isCreatingOffice,
  officeId,
  setCanEditRoomCapacity,
  office,
  onSaved,
  totalSeats,
  deleteAction,
}: Props): React.JSX.Element => {
  const keycloak = useKeycloak();
  const [isSaving, setIsSaving] = useState(false);

  const { companies } = useCompanies();
  const { offices: administeredOffices } = useOfficeManagerOrAdminOffices();

  const isWhyellow = keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE);

  // The companies this user is assigned to, derived from the offices they administer — a user can
  // span several. They cannot read the company list itself (Whyellow-only), so this is the set they
  // are allowed to choose from; only a super-admin gets every company in the system.
  const assignedCompanies = useMemo(() => {
    const seen: Record<string, boolean> = {};
    const unique: { id: string; name: string }[] = [];

    (administeredOffices ?? []).forEach((administered) => {
      const id = administered.companyId;
      if (id != null && !seen[id]) {
        seen[id] = true;
        unique.push({ id, name: administered.companyName ?? '' });
      }
    });

    return unique.sort((a, b) => a.name.localeCompare(b.name));
  }, [administeredOffices]);

  const companyOptions = useMemo(() => {
    if (isWhyellow) {
      return (companies ?? []).map((company) => ({ id: company.id ?? '', name: company.name }));
    }
    return assignedCompanies;
  }, [isWhyellow, companies, assignedCompanies]);

  const isMobile = useContext(IsMobileContext);
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [isEdited, setIsEdited] = useState(false);
  const [errors, setErrors] = useState(new Map<OfficeFormType, string | boolean>());

  const [officeToUpdate, setOfficeToUpdate] = useState<Office | undefined>(undefined);

  const navigate = useNavigate();
  const navigateToOverview = NavigateToEdit(navigate, [OFFICE_INFORMATION_KEY]);

  const handleChange = (formType: OfficeFormType, value: unknown): void => {
    if (officeToUpdate === undefined) {
      return;
    }

    const officeValidation = ApplyValueToOffice(formType, value, officeToUpdate);

    setErrors(officeValidation.errors);
    try {
      assert.deepStrictEqual(office, officeValidation.office);
      if (isEmpty(officeValidation.errors)) {
        setIsEdited(false);
      }
    } catch (error) {
      setIsEdited(true);
    }
    setOfficeToUpdate(officeValidation.office);
  };

  const saveOffice = useCallback(
    async (newOffice: Office): Promise<void> => {
      if (!isEmpty(errors)) {
        return;
      }
      // The error under the field is cleared by the next edit to any other field, so check the value itself.
      const groupWindowError = groupPlanPeriodDaysError(newOffice);
      if (groupWindowError != null) {
        notify(groupWindowError, false);
        return;
      }
      if (stringIsNullOrWhitespace(newOffice.name)) {
        notify(t('name-empty-message'), false);
        return;
      }
      if (stringIsNullOrWhitespace(newOffice.companyId)) {
        notify(t('company-empty-message'), false);
        return;
      }
      if (!isThresholdPairValid(newOffice.thresholdMinPercentage, newOffice.thresholdMaxPercentage)) {
        notify(t('dashboard-thresholds-validation-message'), false);
        return;
      }

      // A group window is only sent while group reservations are on. The API discards it otherwise, but a
      // stale value left in the hidden field should not travel at all. Omitted means "none" to the API.
      const officeToSave: Office = newOffice.groupReservationsEnabled
        ? newOffice
        : { ...newOffice, groupPlanPeriodDays: undefined };

      setIsSaving(true);
      let officeResult;
      if (isCreatingOffice) {
        officeResult = await createOffice(officeToSave, keycloak);
      } else {
        const officeToSend = officeToSave;
        officeToSend.officeId = officeId ?? '';
        officeResult = await updateOffice(officeToSend, keycloak);
      }
      if (officeResult != null) {
        notify(t('office-saved-success-message'), true);
        if (!isCreatingOffice) {
          onSaved?.();
        }
      } else {
        notify(t('office-saved-error-message'), false);
      }
      setIsSaving(false);
      setIsEdited(false);

      if (isCreatingOffice) {
        if (officeResult?.id) {
          navigateToOverview(officeResult.id);
        }
      }
    },
    [keycloak, t, errors, isCreatingOffice, navigateToOverview, officeId, onSaved],
  );

  useEffect(() => {
    if (office != null && officeToUpdate == null) {
      if (isLoading) {
        setIsLoading(false);
      }

      const officeInLocalStorage = localStorage.getItem(OFFICE_INFORMATION_KEY);
      setOfficeToUpdate(officeInLocalStorage ? JSON.parse(officeInLocalStorage) : office);

      if (officeInLocalStorage) {
        try {
          assert.deepStrictEqual(JSON.parse(officeInLocalStorage), office);
          setIsEdited(false);
        } catch (error) {
          if (error) {
            setIsEdited(true);
          }
        }
      }
    }
  }, [office, officeToUpdate, isLoading]);

  useEffect(() => {
    UpdateLocalStorageWithOffice(isEdited, OFFICE_INFORMATION_KEY, officeToUpdate);

    if (officeToUpdate != null) {
      setCanEditRoomCapacity(!officeToUpdate.floorPlanEnabled);
    }
  }, [officeToUpdate, isEdited, setCanEditRoomCapacity]);

  const infoIcon = (tooltip: string): React.JSX.Element => (
    <InfoOutlinedIcon data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID} data-tooltip-content={tooltip} sx={styles.tooltipIconSX} />
  );

  const renderSwitchRow = (
    isFirst: boolean,
    label: React.ReactNode,
    description: React.ReactNode,
    checked: boolean,
    onChange: (value: boolean) => void,
    options?: { disabled?: boolean; caution?: string; children?: React.ReactNode },
  ): React.JSX.Element => (
    <div className={style(formStyles.switchRow(isFirst))}>
      <div className={style(formStyles.switchRowBody)}>
        <div className={style(formStyles.switchRowName)}>
          {label}
          {options?.caution != null && checked && <span className={style(formStyles.cautionChip)}>{options.caution}</span>}
        </div>
        {description != null && <span className={style(formStyles.switchRowDescription)}>{description}</span>}
        {options?.children}
      </div>
      <div className={style(formStyles.switchRowControl)}>
        <Switch
          checked={checked}
          disabled={options?.disabled ?? false}
          onChange={onChange}
          onColor={StyleConstants.ORANGE_COLOR}
          onHandleColor={StyleConstants.WHITE_COLOR}
          handleDiameter={styles.switchDiameter}
          uncheckedIcon={false}
          checkedIcon={false}
          boxShadow={styles.switchBoxShadow}
          activeBoxShadow={styles.switchActiveBoxShadow}
          height={styles.switchHeight}
          width={styles.switchWidth}
        />
      </div>
    </div>
  );

  if (isLoading || officeToUpdate == null) {
    return (
      <div className={style(formStyles.sectionCard)}>
        <Center>
          <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
        </Center>
      </div>
    );
  }

  const preferredPercentage = clampPercentage(Number(officeToUpdate.preferredCapacityPercentage) || 0);
  const openSeats = totalSeats != null ? Math.round((totalSeats * preferredPercentage) / 100) : undefined;

  const thresholdMin = officeToUpdate.thresholdMinPercentage;
  const thresholdMax = officeToUpdate.thresholdMaxPercentage;
  const thresholdsValid = isThresholdPairValid(thresholdMin, thresholdMax);
  const hasThresholdRange = thresholdsValid && thresholdMin != null && thresholdMax != null;

  const bookableUntil = new Date();
  bookableUntil.setDate(bookableUntil.getDate() + (Number(officeToUpdate.planPeriodDays) || 0));
  const bookableUntilLabel = bookableUntil.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });

  // Only shown when it actually extends the fill-in period. Computed exactly like bookableUntil above,
  // so the two dates on this card can never disagree about how a window is counted.
  // Only a valid group window is described: while the field holds a half-typed or out-of-range value the
  // callout and the summary keep showing the fill-in period, not a date that would not be saved.
  const planPeriodDays = Number(officeToUpdate.planPeriodDays) || 0;
  const groupWindowIsValid = groupPlanPeriodDaysError(officeToUpdate) == null;
  const groupPlanPeriodDays =
    officeToUpdate.groupReservationsEnabled && groupWindowIsValid
      ? Math.max(planPeriodDays, Number(officeToUpdate.groupPlanPeriodDays) || planPeriodDays)
      : planPeriodDays;
  const hasGroupWindow = groupPlanPeriodDays > planPeriodDays;
  const groupBookableUntil = new Date();
  groupBookableUntil.setDate(groupBookableUntil.getDate() + groupPlanPeriodDays);
  const groupBookableUntilLabel = groupBookableUntil.toLocaleDateString(i18n.language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const enabledFeatures = [
    { enabled: officeToUpdate.lunchEnabled, label: t('show-lunch-option') },
    { enabled: officeToUpdate.qrCodesEnabled, label: t('use-qr-codes') },
    { enabled: officeToUpdate.floorPlanEnabled, label: t('use-floor-plan') },
    { enabled: officeToUpdate.groupReservationsEnabled, label: t('reserve-as-group') },
  ].filter((feature) => feature.enabled);

  const webLinkPreview = (officeToUpdate.webLabel ?? '').trim() || (officeToUpdate.webUrl ?? '').trim();

  return (
    <div className={style(formStyles.formGrid(isMobile))}>
      <div>
        {/* Identity */}
        <section className={style(formStyles.sectionCard)}>
          <h2 className={style(formStyles.sectionTitle)}>{t('office-section-identity')}</h2>
          <span className={style(formStyles.sectionDescription)}>{t('office-section-identity-description')}</span>
          <div className={style(formStyles.sectionContent)}>
            <div>
              <span className={style(formStyles.fieldLabel)}>{t('office-name')}</span>
              <input
                type="text"
                maxLength={320}
                className={style(formStyles.textInput)}
                value={officeToUpdate.name}
                onChange={(event): void => handleChange(OfficeFormType.name, event.target.value)}
              />
            </div>
            <div>
              <span className={style(formStyles.fieldLabel)}>{t('company')}</span>
              <Select
                size="small"
                displayEmpty
                sx={{ maxWidth: 480, width: '100%' }}
                value={officeToUpdate.companyId ?? ''}
                onChange={(event: SelectChangeEvent): void => handleChange(OfficeFormType.company, event.target.value)}
              >
                <MenuItem value="" disabled>
                  {t('select-company')}
                </MenuItem>
                {companyOptions.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
              {stringIsNullOrWhitespace(officeToUpdate.companyId) && (
                <span className={style(formStyles.fieldError)}>{t('company-empty-message')}</span>
              )}
            </div>
            <div className={style(formStyles.divider)} />
            {renderSwitchRow(
              true,
              t('visible-in-app'),
              t('office-visible-in-app-description'),
              officeToUpdate.isAvailableInApp,
              (value) => handleChange(OfficeFormType.isAvailableInApp, value),
            )}
          </div>
        </section>

        {/* Capacity */}
        <section className={style(formStyles.sectionCard)}>
          <h2 className={style(formStyles.sectionTitle)}>{t('office-section-capacity')}</h2>
          <span className={style(formStyles.sectionDescription)}>{t('office-section-capacity-description')}</span>
          <div className={style(formStyles.sectionContent)}>
            <div>
              <span className={style(formStyles.fieldLabel)}>
                {t('preferred-capacity')}
                {infoIcon(t('preferred-capacity-info-message').toString())}
              </span>
              <div className={style(formStyles.sliderRow)}>
                <input
                  type="range"
                  min={1}
                  max={100}
                  step={1}
                  className={style(formStyles.slider)}
                  aria-label={t('preferred-capacity').toString()}
                  value={preferredPercentage}
                  onChange={(event): void => handleChange(OfficeFormType.maxCapacity, Number(event.target.value))}
                />
                <div className={style(formStyles.adorned(108))}>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    step={1}
                    className={style(formStyles.adornedInput)}
                    aria-label={t('preferred-capacity').toString()}
                    value={officeToUpdate.preferredCapacityPercentage}
                    onChange={(event): void => handleChange(OfficeFormType.maxCapacity, Number(event.target.value))}
                  />
                  <span className={style(formStyles.adornmentUnit)}>%</span>
                </div>
              </div>
              {errors.get(OfficeFormType.maxCapacity) === true && (
                <span className={style(formStyles.fieldError)}>{t('capacity-percentage-numbers-only-message')}</span>
              )}
              {openSeats != null && totalSeats != null && (
                <span className={style(formStyles.fieldHint)}>
                  {t('office-capacity-seats-hint').replace('[x]', openSeats.toString()).replace('[y]', totalSeats.toString())}
                </span>
              )}
            </div>

            <div>
              <span className={style(formStyles.fieldLabel)}>{t('office-occupancy-thresholds')}</span>
              <div className={style(formStyles.fieldRow(isMobile))}>
                <div className={style(formStyles.fieldColumn)}>
                  <span className={style(formStyles.fieldLabel)}>
                    {t('threshold-min')}
                    {infoIcon(t('threshold-min-info-message').toString())}
                  </span>
                  <div className={style(formStyles.adorned())}>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className={style(formStyles.adornedInput)}
                      value={thresholdMin ?? ''}
                      onChange={(event): void =>
                        handleChange(
                          OfficeFormType.officeThresholdMinPercentage,
                          event.target.value === '' ? undefined : Number(event.target.value),
                        )
                      }
                    />
                    <span className={style(formStyles.adornmentUnit)}>%</span>
                  </div>
                </div>
                <div className={style(formStyles.fieldColumn)}>
                  <span className={style(formStyles.fieldLabel)}>
                    {t('threshold-max')}
                    {infoIcon(t('threshold-max-info-message').toString())}
                  </span>
                  <div className={style(formStyles.adorned())}>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className={style(formStyles.adornedInput)}
                      value={thresholdMax ?? ''}
                      onChange={(event): void =>
                        handleChange(
                          OfficeFormType.officeThresholdMaxPercentage,
                          event.target.value === '' ? undefined : Number(event.target.value),
                        )
                      }
                    />
                    <span className={style(formStyles.adornmentUnit)}>%</span>
                  </div>
                </div>
              </div>
              <div className={style(formStyles.band)}>
                <div className={style(formStyles.bandTarget(preferredPercentage))} />
                {hasThresholdRange && thresholdMax != null && preferredPercentage > thresholdMax && (
                  <div className={style(formStyles.bandBusy(thresholdMax, preferredPercentage - thresholdMax))} />
                )}
                {hasThresholdRange && thresholdMin != null && thresholdMax != null && (
                  <div className={style(formStyles.bandRange(thresholdMin, thresholdMax - thresholdMin))} />
                )}
              </div>
              <div className={style(formStyles.bandLegend)}>
                <span>0%</span>
                <span>
                  {hasThresholdRange && thresholdMin != null && thresholdMax != null
                    ? t('office-band-comfortable')
                      .replace('[x]', thresholdMin.toString())
                      .replace('[y]', thresholdMax.toString())
                    : t('office-band-unset')}
                </span>
                <span>100%</span>
              </div>
              {!thresholdsValid && <span className={style(formStyles.fieldError)}>{t('dashboard-thresholds-validation-message')}</span>}
            </div>

            <div className={style(formStyles.divider)} />
            {renderSwitchRow(
              true,
              t('capacity-is-limit'),
              t('office-capacity-is-limit-description'),
              officeToUpdate.isMaxCapacityTheLimit,
              (value) => handleChange(OfficeFormType.isMaxCapacityTheLimit, value),
            )}
          </div>
        </section>

        {/* Features */}
        <section className={style(formStyles.sectionCard)}>
          <h2 className={style(formStyles.sectionTitle)}>{t('office-section-features')}</h2>
          <span className={style(formStyles.sectionDescription)}>{t('office-section-features-description')}</span>
          <div className={style(formStyles.sectionContent)}>
            <div>
              {renderSwitchRow(true, t('show-lunch-option'), t('office-lunch-description'), officeToUpdate.lunchEnabled, (value) =>
                handleChange(OfficeFormType.lunchEnabled, value))}
              {renderSwitchRow(false, t('use-qr-codes'), t('qr-codes-info-message'), officeToUpdate.qrCodesEnabled, (value) =>
                handleChange(OfficeFormType.qrCodeEnabled, value))}
              {renderSwitchRow(
                false,
                t('use-floor-plan'),
                t('floor-plan-info-message'),
                officeToUpdate.floorPlanEnabled,
                (value) => handleChange(OfficeFormType.floorPlanEnabled, value),
                {
                  disabled: !officeToUpdate.qrCodesEnabled || !officeToUpdate.isFloorPlanAvailable,
                  caution: t('office-floorplan-locks-rooms').toString(),
                },
              )}
              {process.env.REACT_APP_GROUP_RESERVATION_ENABLED === 'true' &&
                renderSwitchRow(
                  false,
                  t('reserve-as-group'),
                  t('reserve-as-group-message'),
                  officeToUpdate.groupReservationsEnabled,
                  (value) => handleChange(OfficeFormType.groupReservationsEnabled, value),
                  {
                    disabled:
                      !officeToUpdate.qrCodesEnabled || !officeToUpdate.isFloorPlanAvailable || !officeToUpdate.floorPlanEnabled,
                    children: officeToUpdate.groupReservationsEnabled && (
                      <div style={{ marginTop: 12 }}>
                        <span className={style(formStyles.fieldLabel)}>
                          {t('maximum-group-size')}
                          {infoIcon(t('maximum-group-size-message').toString())}
                        </span>
                        <div className={style(formStyles.adorned(160))}>
                          <input
                            type="number"
                            className={style(formStyles.adornedInput)}
                            value={officeToUpdate.maximumGroupSize ?? ''}
                            onChange={(event): void => handleChange(OfficeFormType.maximumGroupSize, Number(event.target.value))}
                          />
                        </div>
                        {errors.get(OfficeFormType.maximumGroupSize) != null && (
                          <span className={style(formStyles.fieldError)}>{errors.get(OfficeFormType.maximumGroupSize)}</span>
                        )}
                        <span className={style(formStyles.fieldLabel)} style={{ marginTop: 12 }}>
                          {t('maximum-group-leaders')}
                          {infoIcon(t('maximum-group-leaders-message').toString())}
                        </span>
                        <div className={style(formStyles.adorned(160))}>
                          <input
                            type="number"
                            className={style(formStyles.adornedInput)}
                            value={officeToUpdate.maximumGroupLeaders ?? ''}
                            onChange={(event): void => handleChange(OfficeFormType.maximumGroupLeaders, Number(event.target.value))}
                          />
                        </div>
                        {errors.get(OfficeFormType.maximumGroupLeaders) != null && (
                          <span className={style(formStyles.fieldError)}>{errors.get(OfficeFormType.maximumGroupLeaders)}</span>
                        )}
                      </div>
                    ),
                  },
                )}
            </div>
            {officeToUpdate.floorPlanEnabled && (
              <div className={style(formStyles.calloutInfo)}>
                <span className={style(formStyles.calloutIcon)}>
                  <LockOutlineIcon size={18} />
                </span>
                <div>{t('room-not-edditable-warning')}</div>
              </div>
            )}
          </div>
        </section>

        {/* Booking window */}
        <section className={style(formStyles.sectionCard)}>
          <h2 className={style(formStyles.sectionTitle)}>{t('office-section-booking-window')}</h2>
          <span className={style(formStyles.sectionDescription)}>{t('office-section-booking-window-description')}</span>
          <div className={style(formStyles.sectionContent)}>
            <div className={style(formStyles.fieldRow(isMobile))}>
              <div className={style(formStyles.fieldColumn)}>
                <span className={style(formStyles.fieldLabel)}>
                  {t('plan-period-days')}
                  {infoIcon(t('plan-period-days-info-message').toString())}
                </span>
                <div className={style(formStyles.adorned())}>
                  <input
                    type="number"
                    className={style(formStyles.adornedInput)}
                    value={officeToUpdate.planPeriodDays}
                    onChange={(event): void => handleChange(OfficeFormType.planPeriodDays, Number(event.target.value))}
                  />
                </div>
                {errors.get(OfficeFormType.planPeriodDays) != null && (
                  <span className={style(formStyles.fieldError)}>{errors.get(OfficeFormType.planPeriodDays)}</span>
                )}
              </div>
              <div className={style(formStyles.fieldColumn)}>
                <label htmlFor="group-plan-period-days" className={style(formStyles.fieldLabel)}>
                  {t('group-plan-period-days')}
                  {infoIcon(t('group-plan-period-days-info-message').toString())}
                </label>
                <div className={style(formStyles.adorned())}>
                  <input
                    id="group-plan-period-days"
                    type="number"
                    min={1}
                    step={1}
                    aria-describedby="group-plan-period-days-help"
                    aria-invalid={errors.get(OfficeFormType.groupPlanPeriodDays) != null}
                    className={style(formStyles.adornedInput)}
                    disabled={!officeToUpdate.groupReservationsEnabled}
                    placeholder={
                      officeToUpdate.groupReservationsEnabled && officeToUpdate.planPeriodDays != null
                        ? String(officeToUpdate.planPeriodDays)
                        : ''
                    }
                    value={officeToUpdate.groupReservationsEnabled ? officeToUpdate.groupPlanPeriodDays ?? '' : ''}
                    onChange={(event): void =>
                      handleChange(
                        OfficeFormType.groupPlanPeriodDays,
                        event.target.value === '' ? undefined : Number(event.target.value),
                      )
                    }
                  />
                </div>
                {errors.get(OfficeFormType.groupPlanPeriodDays) != null ? (
                  <span id="group-plan-period-days-help" role="alert" className={style(formStyles.fieldError)}>
                    {errors.get(OfficeFormType.groupPlanPeriodDays)}
                  </span>
                ) : (
                  <span id="group-plan-period-days-help" className={style(formStyles.fieldHint)}>
                    {officeToUpdate.groupReservationsEnabled
                      ? t('group-plan-period-days-hint').replace('[x]', String(officeToUpdate.planPeriodDays))
                      : t('group-plan-period-days-disabled-hint')}
                  </span>
                )}
              </div>
              <div className={style(formStyles.fieldColumn)}>
                <span className={style(formStyles.fieldLabel)}>
                  {t('booking-timezone')}
                  {infoIcon(t('booking-timezone-info-message').toString())}
                </span>
                <TimezoneSelector
                  value={officeToUpdate.bookingTimeZone ?? defaultBookingTimeZone}
                  onChange={(timeZone): void => handleChange(OfficeFormType.bookingTimeZone, timeZone)}
                  disabled={false}
                  searchPlaceholder={t('booking-timezone-search').toString()}
                />
                <span className={style(formStyles.fieldHint)}>{t('booking-timezone-hint')}</span>
              </div>
            </div>
            <div className={style(formStyles.callout)}>
              <span className={style(formStyles.calloutIcon)}>
                <CalendarClockIcon size={18} />
              </span>
              <div>
                {t('office-bookable-until-callout')
                  .replace('[x]', bookableUntilLabel)
                  .replace('[y]', officeToUpdate.bookingTimeZone ?? defaultBookingTimeZone)}
              </div>
            </div>
            {hasGroupWindow && (
              <div className={style(formStyles.callout)}>
                <span className={style(formStyles.calloutIcon)}>
                  <AccountGroupOutlineIcon size={18} />
                </span>
                <div>{t('office-group-bookable-until-callout').replace('[x]', groupBookableUntilLabel)}</div>
              </div>
            )}
          </div>
        </section>

        {/* Homepage link */}
        <section className={style(formStyles.sectionCard)}>
          <h2 className={style(formStyles.sectionTitle)}>{t('office-section-homepage-link')}</h2>
          <span className={style(formStyles.sectionDescription)}>{t('office-section-homepage-link-description')}</span>
          <div className={style(formStyles.sectionContent)}>
            <div>
              <span className={style(formStyles.fieldLabel)}>
                {t('weblink-homepage')}
                {infoIcon(t('weblink-info-message').toString())}
              </span>
              <input
                type="text"
                maxLength={350}
                className={style(formStyles.textInput)}
                placeholder={t('add-weblink').toString()}
                value={officeToUpdate.webUrl ?? ''}
                onChange={(event): void => handleChange(OfficeFormType.webUrl, event.target.value)}
              />
            </div>
            <div>
              <span className={style(formStyles.fieldLabel)}>
                {t('weblink-label')}
                {infoIcon(t('weblink-name-message').toString())}
              </span>
              <input
                type="text"
                maxLength={60}
                className={style(formStyles.textInput)}
                placeholder={t('add-label-text').toString()}
                value={officeToUpdate.webLabel ?? ''}
                onChange={(event): void => handleChange(OfficeFormType.webLabel, event.target.value)}
              />
              {webLinkPreview !== '' && (
                <span className={style(formStyles.fieldHint)}>
                  {t('office-link-shows-as')} <span className={style(formStyles.chip)}>{webLinkPreview}</span>
                </span>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Summary rail */}
      <aside className={style(formStyles.rail(isMobile))}>
        <span className={style(formStyles.railTitle)}>{t('office-summary')}</span>

        <div className={style(formStyles.railBlock(true))}>
          <span className={style(formStyles.railKey)}>{t('office-summary-open-for-booking')}</span>
          <div className={style(formStyles.railBig)}>
            <b className={style(formStyles.railBigValue)}>{openSeats ?? `${preferredPercentage}%`}</b>
            <span className={style(formStyles.railBigUnit)}>
              {totalSeats != null ? t('office-summary-of-seats').replace('[x]', totalSeats.toString()) : t('preferred-capacity')}
            </span>
          </div>
        </div>

        <div className={style(formStyles.railBlock(false))}>
          <span className={style(formStyles.railKey)}>{t('office-summary-bookable-until')}</span>
          <span className={style(formStyles.railValue)}>{bookableUntilLabel}</span>
        </div>

        {hasGroupWindow && (
          <div className={style(formStyles.railBlock(false))}>
            <span className={style(formStyles.railKey)}>{t('office-summary-group-bookable-until')}</span>
            <span className={style(formStyles.railValue)}>{groupBookableUntilLabel}</span>
          </div>
        )}

        <div className={style(formStyles.railBlock(false))}>
          <span className={style(formStyles.railKey)}>{t('office-summary-features-on')}</span>
          {enabledFeatures.length > 0 ? (
            <div className={style(formStyles.railTags)}>
              {enabledFeatures.map((feature) => (
                <span key={feature.label} className={style(formStyles.chip)}>
                  {feature.label}
                </span>
              ))}
            </div>
          ) : (
            <span className={style(formStyles.railValue)}>{t('office-summary-no-features')}</span>
          )}
        </div>

        <div className={style(formStyles.railSaveBlock)}>
          <span className={style(isEdited ? formStyles.railSaveState : formStyles.railSaveStateIdle)}>
            {isEdited ? t('there-are-unsaved-changes') : t('office-summary-all-saved')}
          </span>
          <PrimaryButton
            fullWidth
            loading={isSaving}
            disabled={!isEdited}
            onClick={(): void => {
              saveOffice(officeToUpdate);
            }}
          >
            {t('save')}
          </PrimaryButton>
          {deleteAction}
        </div>
      </aside>
    </div>
  );
};

export default OfficeInfoGroup;
